import { useEffect, useState } from "react";
import { Calendar, FileText, Loader2, Pencil, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { parentApi } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

// Modal that lets a parent view + edit their kid's info, parent contact info,
// and review the waivers they signed for each registration.
//
// All updates apply across every registration that currently shares the kid's
// participant_name (so a kid in two camps gets both records updated at once).
export default function PlayerDetailsModal({ open, onOpenChange, kid, onSaved }) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waiverTextOpen, setWaiverTextOpen] = useState(null);  // registration object or null

  // Pull the most-recent registration as the source of truth for the editable
  // fields (gender / age / contact info). All registrations for the same kid
  // SHOULD match, but if they don't, the newest wins.
  const registrations = (kid && [...kid.upcoming, ...kid.past]) || [];
  const newest = registrations
    .slice()
    .sort((a, b) => (b.registered_at || "").localeCompare(a.registered_at || ""))[0];

  // Reset form whenever the modal opens or the kid changes
  useEffect(() => {
    if (!open || !newest) return;
    setEditing(false);
    setForm({
      participant_name: newest.participant_name || "",
      age: newest.age ?? "",
      gender: newest.gender || "",
      skill_level: newest.skill_level || "",
      medical_info: newest.medical_info || "",
      parent_name: newest.parent_name || "",
      parent_phone: newest.parent_phone || "",
      emergency_contact_name: newest.emergency_contact_name || "",
      emergency_contact_phone: newest.emergency_contact_phone || "",
    });
  }, [open, newest]);

  if (!kid || !newest) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        current_name: kid.name,                 // identifies the kid in DB before this update
        participant_name: form.participant_name,
        age: form.age === "" ? undefined : Number(form.age),
        gender: form.gender || undefined,
        skill_level: form.skill_level || undefined,
        medical_info: form.medical_info,
        parent_name: form.parent_name,
        parent_phone: form.parent_phone,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
      };
      await parentApi.post("/parent/players/update", payload);
      toast.success(t("parent.detailsSaved"));
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("parent.detailsFail"));
    } finally {
      setSaving(false);
    }
  };

  const waiver = waiverTextOpen ? t("registration.waiverText") : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="font-heading text-xl text-burns-gold">
                {kid.name}
              </DialogTitle>
              {!editing ? (
                <Button size="sm" onClick={() => setEditing(true)} className="bg-burns-red hover:bg-burns-red/80 text-white">
                  <Pencil className="w-4 h-4 mr-1" /> {t("parent.editDetails")}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-400 hover:text-white">
                    <X className="w-4 h-4 mr-1" /> {t("parent.cancel")}
                  </Button>
                  <Button size="sm" onClick={save} disabled={saving} className="bg-burns-red hover:bg-burns-red/80 text-white">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    {t("parent.save")}
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>

          {/* Participant info */}
          <section className="space-y-3 mt-2">
            <p className="text-burns-gold text-xs uppercase tracking-wide">{t("parent.detailsParticipant")}</p>
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsName")}</Label>
                  <Input value={form.participant_name} onChange={(e) => setForm({ ...form, participant_name: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsAge")}</Label>
                  <Input type="number" min="4" max="25" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsGender")}</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger className="bg-zinc-900 border-white/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20 text-white">
                      <SelectItem value="Male">{t("tshirt.male")}</SelectItem>
                      <SelectItem value="Female">{t("tshirt.female")}</SelectItem>
                      <SelectItem value="Other">{t("tshirt.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsSkill")}</Label>
                  <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                    <SelectTrigger className="bg-zinc-900 border-white/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/20 text-white">
                      <SelectItem value="Beginner">{t("registration.beginner")}</SelectItem>
                      <SelectItem value="Intermediate">{t("registration.intermediate")}</SelectItem>
                      <SelectItem value="Advanced">{t("registration.advanced")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsMedical")}</Label>
                  <Textarea rows={2} value={form.medical_info} onChange={(e) => setForm({ ...form, medical_info: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <KV label={t("parent.detailsAge")} value={newest.age} />
                <KV label={t("parent.detailsGender")} value={newest.gender} />
                <KV label={t("parent.detailsSkill")} value={newest.skill_level} />
                <KV label={t("parent.detailsLanguage")} value={newest.language === "fr" ? "Français" : "English"} />
                {newest.medical_info && (
                  <div className="col-span-2 sm:col-span-4">
                    <p className="text-zinc-500 text-xs">{t("parent.detailsMedical")}</p>
                    <p className="text-zinc-200 text-sm whitespace-pre-wrap">{newest.medical_info}</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Parent contact */}
          <section className="space-y-3 mt-5 border-t border-white/5 pt-4">
            <p className="text-burns-gold text-xs uppercase tracking-wide">{t("parent.detailsContact")}</p>
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsParentName")}</Label>
                  <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsParentPhone")}</Label>
                  <Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsEmergencyName")}</Label>
                  <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">{t("parent.detailsEmergencyPhone")}</Label>
                  <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="bg-zinc-900 border-white/20 text-white" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <KV label={t("parent.detailsParentName")} value={newest.parent_name} />
                <KV label={t("parent.detailsParentPhone")} value={newest.parent_phone} />
                <KV label={t("parent.detailsEmergencyName")} value={newest.emergency_contact_name} />
                <KV label={t("parent.detailsEmergencyPhone")} value={newest.emergency_contact_phone} />
              </div>
            )}
          </section>

          {/* Signed waivers */}
          <section className="space-y-2 mt-5 border-t border-white/5 pt-4">
            <p className="text-burns-gold text-xs uppercase tracking-wide flex items-center gap-1">
              <FileText className="w-3 h-3" /> {t("parent.detailsWaivers")}
            </p>
            <div className="space-y-1">
              {registrations.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setWaiverTextOpen(r)}
                  className="w-full text-left bg-zinc-900 border border-white/5 hover:border-burns-gold rounded px-3 py-2 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-zinc-200 text-sm truncate">{r.camp_name}</p>
                    <p className="text-zinc-500 text-xs">
                      {r.waiver_signed
                        ? t("parent.waiverSignedOn").replace("{date}", r.waiver_signed_date || "—").replace("{name}", r.waiver_signer_name || "—")
                        : t("parent.waiverNotSigned")}
                    </p>
                  </div>
                  <span className="text-burns-gold text-xs uppercase tracking-wide">{t("parent.waiverView")}</span>
                </button>
              ))}
            </div>
            <p className="text-zinc-600 text-xs">{t("parent.waiverNote")}</p>
          </section>
        </DialogContent>
      </Dialog>

      {/* Waiver viewer */}
      <Dialog open={!!waiverTextOpen} onOpenChange={(o) => { if (!o) setWaiverTextOpen(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-burns-gold">
              {t("parent.waiverModalTitle")}
            </DialogTitle>
            {waiverTextOpen && (
              <p className="text-zinc-500 text-xs">
                {waiverTextOpen.camp_name}
                {waiverTextOpen.waiver_signed && (
                  <> · {t("parent.waiverSignedShort").replace("{date}", waiverTextOpen.waiver_signed_date || "—").replace("{name}", waiverTextOpen.waiver_signer_name || "—")}</>
                )}
              </p>
            )}
          </DialogHeader>
          <div className="bg-zinc-900 border border-white/10 rounded-md p-4 mt-2">
            <p className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed">{waiver}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <p className="text-zinc-500 text-xs">{label}</p>
      <p className="text-zinc-200 text-sm">{value || <span className="text-zinc-600">—</span>}</p>
    </div>
  );
}
